import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper, Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import type { User } from "components/userModel";
import { getJaDisplayName } from "components/userUtil";
import { updateUserEmail } from "./updateUserEmail";

interface Props {
  user: User,
  refresh: () => void,
}

export default function EmailEditComponent({ user: initialUser, refresh }: Props) {
  const [email, setEmail] = useState(initialUser.email);

  // Loading state
  const [isLoading, setIsLoading] = useState(false);

  // Toast state
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Handle update button click
  const handleUpdate = async () => {
    if (!window.confirm("会員情報を更新しますか？")) {
      return;
    }

    setIsLoading(true);

    try {
      await updateUserEmail({
        email,
        userId: initialUser.id,
      });

      refresh();
      setShowSuccessToast(true);
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "会員情報の更新中にエラーが発生しました。"
      );
      setShowErrorToast(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Toast close handlers
  const handleCloseSuccessToast = () => {
    setShowSuccessToast(false);
  };

  const handleCloseErrorToast = () => {
    setShowErrorToast(false);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  return (
    <>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          メールアドレス
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          変更した場合、対象のユーザーはログアウトします
        </Typography>

        <Grid container spacing={3} sx={{ mb: 5 }}>
          {/* Basic Information */}
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={getJaDisplayName("email")}
              name="email"
              value={email}
              onChange={handleEmailChange}
              margin="normal"
            />
          </Grid>
        </Grid>
        <Box>
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpdate}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
          >
            {isLoading ? "更新中..." : "更新"}
          </Button>
        </Box>
      </Paper>

      {/* Success Toast */}
      <Snackbar
        open={showSuccessToast}
        autoHideDuration={6000}
        onClose={handleCloseSuccessToast}
      >
        <Alert
          onClose={handleCloseSuccessToast}
          severity="success"
          sx={{ width: "100%" }}
        >
          会員情報を正常に更新しました。
        </Alert>
      </Snackbar>

      {/* Error Toast */}
      <Snackbar
        open={showErrorToast}
        autoHideDuration={6000}
        onClose={handleCloseErrorToast}
      >
        <Alert
          onClose={handleCloseErrorToast}
          severity="error"
          sx={{ width: "100%" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
}
